/**
 * API Client utility for making authenticated API calls
 * Includes Firebase authentication token in requests
 */

import { auth } from '../firebase';

/**
 * Get Firebase authentication token
 * @returns {Promise<string|null>} Firebase ID token or null if not authenticated
 */
export const getAuthToken = async () => {
  try {
    const user = auth.currentUser;
    if (user) {
      console.log('[getAuthToken] User found:', user.uid);
      const token = await user.getIdToken();
      if (token) {
        console.log('[getAuthToken] Token retrieved successfully');
        return token;
      } else {
        console.error('[getAuthToken] Token is null or undefined');
        return null;
      }
    } else {
      console.warn('[getAuthToken] No user found. User is not authenticated.');
      return null;
    }
  } catch (error) {
    console.error('[getAuthToken] Error getting auth token:', error);
    console.error('[getAuthToken] Error name:', error.name);
    console.error('[getAuthToken] Error message:', error.message);
    return null;
  }
};

/**
 * Make an authenticated API request with timeout handling
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options (method, body, headers, timeout, etc.)
 * @param {number} options.timeout - Request timeout in milliseconds (default: 600000 = 10 minutes for direct function calls)
 * @returns {Promise<Response>} Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = await getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add authorization header if token is available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Set timeout for request (default: 10 minutes for direct function calls, 50 seconds for hosting rewrites)
  // Firebase Hosting has a 60-second timeout, so we set 50 seconds to fail gracefully
  // Direct function calls have 540-second (9 minute) timeout, so we use 10 minutes (600 seconds) as default
  const timeout = options.timeout || (url.includes('cloudfunctions.net') ? 600000 : 50000);
  
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  try {
    // Remove signal from options if it exists (we'll use our own)
    const { signal: _, timeout: __, ...fetchOptions } = options;
    
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include', // Include cookies for CORS
      signal: controller.signal, // Add abort signal for timeout
    });

    clearTimeout(timeoutId);

    // Handle 401 Unauthorized - token might be expired
    if (response.status === 401) {
      console.warn('API request unauthorized. Token may be expired.');
      // Optionally refresh token and retry
      try {
        const user = auth.currentUser;
        if (user) {
          const newToken = await user.getIdToken(true); // Force token refresh
          headers['Authorization'] = `Bearer ${newToken}`;
          
          // Create new controller for retry
          const retryController = new AbortController();
          const retryTimeoutId = setTimeout(() => {
            retryController.abort();
          }, timeout);
          
          try {
            const { signal: _, timeout: __, ...retryFetchOptions } = options;
            const retryResponse = await fetch(url, {
              ...retryFetchOptions,
              headers,
              credentials: 'include',
              signal: retryController.signal,
            });
            clearTimeout(retryTimeoutId);
            return retryResponse;
          } catch (retryError) {
            clearTimeout(retryTimeoutId);
            throw retryError;
          }
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Handle timeout errors
    if (error.name === 'AbortError') {
      const errorMessage = url.includes('cloudfunctions.net') 
        ? 'Request timed out after 10 minutes. The operation may still be processing. Please check back later.'
        : 'Request timed out after 50 seconds. This endpoint requires direct function calls. Please contact support.';
      throw new Error(errorMessage);
    }
    
    throw error;
  }
};

/**
 * Make an authenticated API request with JSON body
 * @param {string} url - API endpoint URL
 * @param {object} data - Request body data
 * @param {object} options - Additional fetch options
 * @param {number} options.timeout - Request timeout in milliseconds (default: 600000 = 10 minutes for direct function calls)
 * @returns {Promise<Response>} Fetch response
 */
export const authenticatedPost = async (url, data, options = {}) => {
  return authenticatedFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Make an authenticated API request with FormData
 * @param {string} url - API endpoint URL
 * @param {FormData} formData - FormData object
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>} Fetch response
 */
export const authenticatedPostFormData = async (url, formData, options = {}) => {
  try {
    const token = await getAuthToken();
    
    if (!token) {
      console.error('[authenticatedPostFormData] No auth token available');
      throw new Error('Not authenticated. Please sign in to upload files.');
    }
    
    const headers = {
      ...options.headers,
    };

    // Add authorization header if token is available
    // Don't set Content-Type for FormData - browser will set it with boundary
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('[authenticatedPostFormData] Token included in request');
    } else {
      console.error('[authenticatedPostFormData] No token available');
    }

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'include',
      ...options,
    });

    // Handle 401 Unauthorized - token might be expired
    if (response.status === 401) {
      console.warn('[authenticatedPostFormData] API request unauthorized. Token may be expired.');
      try {
        const user = auth.currentUser;
        if (user) {
          console.log('[authenticatedPostFormData] Refreshing token...');
          const newToken = await user.getIdToken(true); // Force token refresh
          if (newToken) {
            headers['Authorization'] = `Bearer ${newToken}`;
            console.log('[authenticatedPostFormData] Retrying request with refreshed token...');
            return fetch(url, {
              method: 'POST',
              body: formData,
              headers,
              credentials: 'include',
              ...options,
            });
          } else {
            throw new Error('Failed to refresh token. Please sign in again.');
          }
        } else {
          throw new Error('No user found. Please sign in again.');
        }
      } catch (error) {
        console.error('[authenticatedPostFormData] Error refreshing token:', error);
        throw new Error('Authentication failed. Please sign in again.');
      }
    }

    return response;
  } catch (error) {
    console.error('[authenticatedPostFormData] Error:', error);
    throw error;
  }
};

/**
 * Handle API response and parse JSON
 * @param {Response} response - Fetch response
 * @returns {Promise<object>} Parsed JSON response
 */
export const handleApiResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch (e) {
      errorData = { message: errorText || `API error: ${response.status}` };
    }
    throw new Error(errorData.message || errorData.error || `API error: ${response.status}`);
  }
  return response.json();
};

